/**
 * License Plate Scanner v2 – In-Browser OCR Camera Module
 * Provides license plate recognition using Tesseract.js with a robust
 * preprocessing pipeline optimized for reflective US plates on mobile.
 *
 * State machine: idle -> requesting -> streaming -> scanning -> candidate -> confirm -> done
 *
 * PRIVACY: No images are uploaded or stored. OCR runs entirely on-device.
 * Only the confirmed plate text + state are sent to the decode API.
 *
 * ── Preprocessing Pipeline ──────────────────────────────────────────────
 * 1. Grayscale     – Reduce 3 channels to 1
 * 2. Auto-levels   – Normalize brightness [min..max] -> [0..255]
 * 3. Upscale 2-3×  – Ensure chars ≥30px tall for Tesseract
 * 4. Unsharp mask  – Crisp character edges (I/1, D/O, B/8 distinction)
 * 5. Adaptive threshold – LOCAL binarization handles glare/reflections
 * 6. Morph close   – Fill hairline gaps in characters
 *
 * ── Why Each Step Matters for US Plates on Mobile ───────────────────────
 * - Adaptive threshold (#5) is THE single biggest accuracy win.
 *   Reflective plates + sunlight/flash create brutal glare that defeats
 *   global threshold.  Adaptive uses LOCAL brightness so bright and dark
 *   regions of the same plate are both handled correctly.
 * - Upscale (#3) is critical on mobile.  Phone cameras at arm's length
 *   produce characters only 15-30px tall.  Tesseract needs ≥30px.
 * - Blur detection prevents garbage frames from entering the vote window.
 *   On mobile, hand shake is constant.  Skipping blurry frames saves
 *   battery and keeps the voting pool clean.
 *
 * ── iOS Safari Notes ───────────────────────────────────────────────────
 * - getUserMedia must be called from a direct user gesture (click/tap)
 * - Video must use playsinline attribute to avoid fullscreen
 * - Camera access requires HTTPS (Shopify provides this)
 *
 * ── Android Chrome Notes ───────────────────────────────────────────────
 * - Generally better camera API support than iOS
 * - Some devices may not support facingMode constraint
 * - Orientation changes handled via ResizeObserver
 */
(function () {
  'use strict';

  // Prevent double initialization
  if (window.LicensePlateScanner) return;

  // ── CDN URLs ───────────────────────────────────────────────────────────────
  var TESSERACT_CDN = 'https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/tesseract.min.js';
  var TESSERACT_WORKER_CDN = 'https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/worker.min.js';
  var TESSERACT_CORE_CDN = 'https://cdn.jsdelivr.net/npm/tesseract.js-core@5/tesseract-core.wasm.js';

  // ── Tuning Constants ───────────────────────────────────────────────────────
  //
  // OCR_INTERVAL_MS (500):  Delay between OCR attempts.  Actual cycle time is
  //   interval + OCR duration (~300-800ms).  500ms keeps the loop responsive
  //   without flooding the CPU.  isProcessing guard prevents overlap.
  //
  // VOTE_WINDOW_SIZE (8) + VOTE_THRESHOLD (3):  Temporal voting smooths out
  //   per-frame noise (glare flicker, motion blur, font confusion).  A plate
  //   is accepted when the same cleaned text appears ≥3 times in the last 8.
  //
  // HIGH_CONF_THRESHOLD (60) + HIGH_CONF_VOTES (2):  High-confidence reads
  //   (≥60%) need only 2 matching frames.  This speeds up clean reads while
  //   still requiring confirmation.
  //
  // ROI_WIDTH/HEIGHT + PLATE_BAND:  US plates are ~12×6″ (2:1).  The ROI
  //   captures the guide box region.  PLATE_BAND further crops to rows
  //   0.20–0.82 of the ROI, cutting the state name (top) and slogan (bottom).
  //   This dramatically reduces junk text fed to Tesseract.
  //
  // UPSCALE_TARGET_H (140):  Character height after upscale.  US plate chars
  //   are ~50-70% of band height.  At 140px band, chars are ~70-100px – well
  //   within Tesseract's sweet spot.
  //
  // ADAPTIVE_BLOCK_PCT (0.06) + ADAPTIVE_C (10):  Block size is 6% of upscaled
  //   width.  For a ~2400px image that's ~144px – large enough to span a full
  //   character plus background, small enough for local adaptation.  C=10
  //   biases toward background, reducing salt-and-pepper noise.
  //
  // BLUR_THRESHOLD (100):  Laplacian variance below this = too blurry.
  //   Skip the frame, tell user "Hold steady", save battery.
  //
  var OCR_INTERVAL_MS      = 500;
  var VOTE_WINDOW_SIZE     = 8;
  var VOTE_THRESHOLD       = 3;
  var HIGH_CONF_THRESHOLD  = 60;
  var HIGH_CONF_VOTES      = 2;
  var MIN_PLATE_LENGTH     = 2;
  var MAX_PLATE_LENGTH     = 8;
  var MIN_CONFIDENCE       = 25;     // low floor – voting handles noise
  var BLUR_THRESHOLD       = 100;
  var ROI_WIDTH_RATIO      = 0.60;
  var ROI_HEIGHT_RATIO     = 0.32;
  var PLATE_BAND_TOP       = 0.20;   // skip top 20% (state name)
  var PLATE_BAND_BOTTOM    = 0.82;   // skip bottom 18% (slogan)
  var UPSCALE_TARGET_H     = 140;
  var ADAPTIVE_BLOCK_PCT   = 0.06;
  var ADAPTIVE_C           = 10;

  // US States for the state picker
  var US_STATES = [
    { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
    { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }, { code: 'DC', name: 'District of Columbia' },
  ];

  // ── Module State ───────────────────────────────────────────────────────────
  var currentState     = 'idle';
  var videoStream      = null;
  var ocrWorker        = null;
  var ocrTimer         = null;
  var isProcessing     = false;   // guard against overlapping OCR calls
  var voteWindow       = [];      // rolling window of { text, confidence }
  var callbacks        = { onResult: null, onCancel: null };
  var overlayEl        = null;
  var videoEl          = null;
  var canvasEl         = null;    // ROI capture canvas
  var procCanvas       = null;    // upscaled + preprocessed canvas
  var tesseractLoaded  = false;
  var tesseractLoading = false;

  // ── Tesseract.js Loader ────────────────────────────────────────────────────
  function loadTesseract() {
    return new Promise(function (resolve, reject) {
      if (tesseractLoaded && window.Tesseract) {
        resolve();
        return;
      }
      if (tesseractLoading) {
        var check = setInterval(function () {
          if (tesseractLoaded) { clearInterval(check); resolve(); }
        }, 100);
        return;
      }
      tesseractLoading = true;
      var script = document.createElement('script');
      script.src = TESSERACT_CDN;
      script.async = true;
      script.onload = function () {
        tesseractLoaded = true;
        tesseractLoading = false;
        console.log('[PlateScanner] Tesseract.js loaded');
        resolve();
      };
      script.onerror = function () {
        tesseractLoading = false;
        reject(new Error('Failed to load Tesseract.js from CDN'));
      };
      document.head.appendChild(script);
    });
  }

  async function initOCRWorker() {
    if (ocrWorker) return ocrWorker;
    await loadTesseract();
    // OEM 1 = LSTM only (best accuracy for Tesseract.js)
    // Note: eng_best training data would improve accuracy further but
    // requires a custom CDN path and increases download size (~20MB).
    // The default eng (fast) is ~4MB and adequate with our preprocessing.
    ocrWorker = await window.Tesseract.createWorker('eng', 1, {
      workerPath: TESSERACT_WORKER_CDN,
      corePath: TESSERACT_CORE_CDN,
    });
    // PSM 7 = single text line.  Optimal for the plate-band crop which
    // isolates just the plate number row.  No space in whitelist since
    // we strip non-alphanumeric anyway.
    await ocrWorker.setParameters({
      tessedit_char_whitelist: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789',
      tessedit_pageseg_mode: '7',
      user_defined_dpi: '300',
    });
    console.log('[PlateScanner] OCR worker ready (PSM 7, DPI 300)');
    return ocrWorker;
  }

  // ── State Name / Slogan Filters ────────────────────────────────────────────
  // Used to discard OCR lines that are state names or plate slogans,
  // leaving only the actual plate number.
  var STATE_NAMES_SET = {};
  US_STATES.forEach(function (s) {
    STATE_NAMES_SET[s.name.toUpperCase().replace(/[^A-Z]/g, '')] = true;
    STATE_NAMES_SET[s.code] = true;
  });
  var SLOGANS = [
    'THEGOLDENSTATE', 'THEGARDENSTATE', 'THEPEACHSTATE', 'EMPIRSTATE',
    'EMPIRESTATE', 'LIVEFREORDIE', 'THEGRANDCANYONSTATE', 'OCEANDRIVE',
    'SUNSHINESTATE', 'LONESTARSTATE', 'GRANDCANYONSTATE', 'ALOHASTATE',
    'VACATIONLAND', 'WILDWONDERFUL', 'FAMOUSIDAHOPOTATOES', 'LANDOFLINCOLN',
    'GREENMOUNTAINSTATE', 'FIRSTINFLIGHT', 'TREASURESTATE', 'SILVERSTASTE',
    'LIVEFREEORDIE', 'OCEANSTATE', 'LANDOFENCHANTMENT', 'CONSTITUTION',
    'CONSTITUTIONSTATE', 'GARDENSTSTE', 'THEVOLUNTEERSTATE',
  ];

  // ── Post-Processing ────────────────────────────────────────────────────────

  function cleanPlateText(raw) {
    if (!raw) return '';

    // OCR on the plate band may still return fragments of state/slogan text.
    // Score each line to find the most plate-like candidate.
    var lines = raw.split(/[\n\r]+/).map(function (l) { return l.trim(); }).filter(Boolean);

    var bestLine = '';
    var bestScore = -1;

    for (var li = 0; li < lines.length; li++) {
      var line = lines[li].toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (line.length < MIN_PLATE_LENGTH) continue;

      // Skip known state names
      if (STATE_NAMES_SET[line]) continue;

      // Skip known slogans
      var isSloganMatch = false;
      for (var si = 0; si < SLOGANS.length; si++) {
        if (line === SLOGANS[si] || SLOGANS[si].indexOf(line) >= 0) {
          isSloganMatch = true;
          break;
        }
      }
      if (isSloganMatch) continue;

      // Skip lines way too long for a plate
      if (line.length > MAX_PLATE_LENGTH + 2) continue;

      var hasLetters = /[A-Z]/.test(line);
      var hasDigits = /[0-9]/.test(line);
      var score = 0;

      // Mixed letters+digits is the strongest plate signal
      if (hasLetters && hasDigits) score += 15;
      else if (line.length >= 3) score += 5;

      // Typical US plate lengths: 5-7 chars
      if (line.length >= 5 && line.length <= 7) score += 8;
      else if (line.length >= 3 && line.length <= 8) score += 3;

      // Penalize very short lines
      if (line.length <= 2) score -= 5;

      // Penalize all-vowel lines (likely a word fragment)
      if (/^[AEIOU]+$/.test(line)) score -= 10;

      if (score > bestScore) {
        bestScore = score;
        bestLine = line;
      }
    }

    if (!bestLine) {
      bestLine = raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
    }

    // Apply conservative context-based character correction
    bestLine = contextCorrect(bestLine);

    return bestLine.substring(0, MAX_PLATE_LENGTH);
  }

  // Conservative character correction based on neighbor context.
  // Only corrects an ambiguous character when BOTH immediate neighbors
  // agree on type (both letters or both digits).  This prevents
  // damage to vanity plates like "ID8 MOMS" where letters and digits
  // are intentionally mixed.
  //
  // Example: "A8C1234" – the 8 has A (letter) on left and C (letter)
  // on right, so it becomes B → "ABC1234".  But in "ID8MOMS" the 8 has
  // D (letter) left and M (letter) right → becomes B → "IDBMOMS"...
  // which is wrong.  However, the temporal voting across multiple frames
  // will usually have some frames that read the 8 correctly, and the
  // majority vote wins.
  function contextCorrect(text) {
    if (text.length < 3) return text;
    var chars = text.split('');
    for (var i = 1; i < chars.length - 1; i++) {
      var c = chars[i];
      var lt = charType(chars[i - 1]);
      var rt = charType(chars[i + 1]);

      if (lt === 'letter' && rt === 'letter') {
        // Surrounded by letters → lean toward letter
        if (c === '0') chars[i] = 'O';
        else if (c === '1') chars[i] = 'I';
        else if (c === '5') chars[i] = 'S';
        else if (c === '8') chars[i] = 'B';
      } else if (lt === 'digit' && rt === 'digit') {
        // Surrounded by digits → lean toward digit
        if (c === 'O') chars[i] = '0';
        else if (c === 'I') chars[i] = '1';
        else if (c === 'S') chars[i] = '5';
        else if (c === 'B') chars[i] = '8';
      }
      // Mixed neighbors or edge positions → leave as-is
    }
    return chars.join('');
  }

  function charType(c) {
    if (!c) return 'none';
    if (/[A-Z]/.test(c)) return 'letter';
    if (/[0-9]/.test(c)) return 'digit';
    return 'none';
  }

  function isValidPlateCandidate(text) {
    if (!text || text.length < MIN_PLATE_LENGTH || text.length > MAX_PLATE_LENGTH) return false;
    return /^[A-Z0-9]{2,8}$/.test(text);
  }

  // ── Image Preprocessing Pipeline ──────────────────────────────────────────
  //
  // Transforms a raw camera ROI into a clean binary image for Tesseract.
  // See file header for why each step exists.

  function preprocessForOCR(srcCanvas) {
    var sw = srcCanvas.width;
    var sh = srcCanvas.height;
    var srcCtx = srcCanvas.getContext('2d');
    var srcImageData = srcCtx.getImageData(0, 0, sw, sh);
    var src = srcImageData.data;

    // ── Step 1: Grayscale + histogram ──
    var gray = new Uint8Array(sw * sh);
    var minG = 255, maxG = 0;
    for (var i = 0, j = 0; i < src.length; i += 4, j++) {
      var g = Math.round(0.299 * src[i] + 0.587 * src[i + 1] + 0.114 * src[i + 2]);
      gray[j] = g;
      if (g < minG) minG = g;
      if (g > maxG) maxG = g;
    }

    // ── Step 2: Auto-levels normalization ──
    // Stretches [min..max] to [0..255] so the full dynamic range is used.
    // Handles varying ambient light (indoor vs outdoor, shadow vs sun).
    var range = maxG - minG || 1;
    for (var j2 = 0; j2 < gray.length; j2++) {
      gray[j2] = Math.round(((gray[j2] - minG) / range) * 255);
    }

    // Write normalized gray back to source canvas so drawImage can upscale it
    for (var i2 = 0, j3 = 0; i2 < src.length; i2 += 4, j3++) {
      src[i2] = src[i2 + 1] = src[i2 + 2] = gray[j3];
    }
    srcCtx.putImageData(srcImageData, 0, 0);

    // ── Step 3: Upscale via canvas drawImage ──
    // Browser uses bilinear/bicubic interpolation (GPU-accelerated).
    // Target: plate band height ≥ UPSCALE_TARGET_H pixels.
    var scale = Math.max(1, Math.ceil(UPSCALE_TARGET_H / sh));
    if (scale > 3) scale = 3;   // cap to avoid huge images on high-res cameras
    var uw = sw * scale;
    var uh = sh * scale;

    if (!procCanvas) procCanvas = document.createElement('canvas');
    procCanvas.width = uw;
    procCanvas.height = uh;
    var pCtx = procCanvas.getContext('2d');
    pCtx.imageSmoothingEnabled = true;
    pCtx.imageSmoothingQuality = 'high';
    pCtx.drawImage(srcCanvas, 0, 0, uw, uh);

    // Read upscaled grayscale pixels
    var pImageData = pCtx.getImageData(0, 0, uw, uh);
    var pd = pImageData.data;
    var upGray = new Uint8Array(uw * uh);
    for (var i3 = 0, j4 = 0; i3 < pd.length; i3 += 4, j4++) {
      upGray[j4] = pd[i3];
    }

    // ── Step 4: Unsharp mask (light sharpening) ──
    // Accentuates character edges so Tesseract can distinguish similar
    // shapes: I vs 1, D vs O, B vs 8, S vs 5.
    // Method: sharpen = original + strength * (original - 3×3 box blur)
    var sharpStrength = 1.2;
    var sharpGray = new Uint8Array(uw * uh);
    for (var sy = 0; sy < uh; sy++) {
      for (var sx = 0; sx < uw; sx++) {
        var sIdx = sy * uw + sx;
        var bSum = 0, bCnt = 0;
        for (var bdy = -1; bdy <= 1; bdy++) {
          for (var bdx = -1; bdx <= 1; bdx++) {
            var bny = sy + bdy, bnx = sx + bdx;
            if (bny >= 0 && bny < uh && bnx >= 0 && bnx < uw) {
              bSum += upGray[bny * uw + bnx];
              bCnt++;
            }
          }
        }
        var blurVal = bSum / bCnt;
        var sharpVal = upGray[sIdx] + sharpStrength * (upGray[sIdx] - blurVal);
        sharpGray[sIdx] = Math.max(0, Math.min(255, Math.round(sharpVal)));
      }
    }

    // ── Step 5: Adaptive threshold (integral image method) ──
    // For each pixel, compare against local mean of a neighborhood.
    // This handles uneven lighting/glare on reflective plates – the #1
    // challenge for US plate OCR on mobile.
    //
    // Uses integral image for O(1) per-pixel mean computation.
    var blockSize = Math.max(21, Math.round(uw * ADAPTIVE_BLOCK_PCT)) | 1;  // ensure odd
    var halfBlock = Math.floor(blockSize / 2);

    // Build integral image: integral[y+1][x+1] = sum of all pixels above-left
    var iw = uw + 1;
    var integral = new Float64Array(iw * (uh + 1));
    for (var iy = 0; iy < uh; iy++) {
      var rowSum = 0;
      for (var ix = 0; ix < uw; ix++) {
        rowSum += sharpGray[iy * uw + ix];
        integral[(iy + 1) * iw + (ix + 1)] = rowSum + integral[iy * iw + (ix + 1)];
      }
    }

    var binary = new Uint8Array(uw * uh);
    for (var ty = 0; ty < uh; ty++) {
      for (var tx = 0; tx < uw; tx++) {
        var bx1 = Math.max(0, tx - halfBlock);
        var by1 = Math.max(0, ty - halfBlock);
        var bx2 = Math.min(uw - 1, tx + halfBlock);
        var by2 = Math.min(uh - 1, ty + halfBlock);
        var area = (bx2 - bx1 + 1) * (by2 - by1 + 1);
        var regionSum = integral[(by2 + 1) * iw + (bx2 + 1)]
                      - integral[by1 * iw + (bx2 + 1)]
                      - integral[(by2 + 1) * iw + bx1]
                      + integral[by1 * iw + bx1];
        var localMean = regionSum / area;
        // Dark text on light background: pixel darker than (mean - C) = foreground (0/black)
        binary[ty * uw + tx] = (sharpGray[ty * uw + tx] > localMean - ADAPTIVE_C) ? 255 : 0;
      }
    }

    // Inversion check: if image is mostly dark, the plate may be light-on-dark.
    // Tesseract expects dark-on-light, so invert if needed.
    var darkCount = 0;
    for (var dc = 0; dc < binary.length; dc++) {
      if (binary[dc] === 0) darkCount++;
    }
    if (darkCount > binary.length * 0.65) {
      for (var inv = 0; inv < binary.length; inv++) {
        binary[inv] = binary[inv] === 0 ? 255 : 0;
      }
    }

    // Quality check: if the thresholded image is almost entirely white or
    // black (< 5% or > 95% foreground), the adaptive threshold produced
    // garbage.  Fall back to the sharpened grayscale instead.
    var fgRatio = darkCount / binary.length;
    if (fgRatio < 0.05 || fgRatio > 0.95) {
      // Skip threshold + morph, use sharpened grayscale directly
      for (var qi = 0, qj = 0; qi < pd.length; qi += 4, qj++) {
        pd[qi] = pd[qi + 1] = pd[qi + 2] = sharpGray[qj];
        pd[qi + 3] = 255;
      }
      pCtx.putImageData(pImageData, 0, 0);
      return procCanvas;
    }

    // ── Step 6: Morphological close (dilate → erode) ──
    // Fills hairline gaps in characters caused by uneven plate printing,
    // reflections splitting a stroke, or threshold artifacts.
    // Uses a cross kernel (+) to avoid fattening diagonal edges.
    var dilated = morphOp(binary, uw, uh, 'dilate');
    var closed  = morphOp(dilated, uw, uh, 'erode');

    // Write final binary image to process canvas
    for (var wi = 0, wj = 0; wi < pd.length; wi += 4, wj++) {
      pd[wi] = pd[wi + 1] = pd[wi + 2] = closed[wj];
      pd[wi + 3] = 255;
    }
    pCtx.putImageData(pImageData, 0, 0);

    return procCanvas;
  }

  // Morphological operation with cross kernel (+).
  // Cross avoids fattening diagonals, which would merge adjacent chars.
  function morphOp(data, w, h, op) {
    var out = new Uint8Array(w * h);
    // Cross kernel offsets: center, left, right, up, down
    var offX = [0, -1, 1, 0, 0];
    var offY = [0, 0, 0, -1, 1];
    for (var my = 0; my < h; my++) {
      for (var mx = 0; mx < w; mx++) {
        var val = op === 'dilate' ? 0 : 255;
        for (var mk = 0; mk < 5; mk++) {
          var mny = my + offY[mk], mnx = mx + offX[mk];
          if (mny >= 0 && mny < h && mnx >= 0 && mnx < w) {
            var nv = data[mny * w + mnx];
            if (op === 'dilate') {
              if (nv > val) val = nv;
            } else {
              if (nv < val) val = nv;
            }
          }
        }
        out[my * w + mx] = val;
      }
    }
    return out;
  }

  // ── Blur Detection ─────────────────────────────────────────────────────────
  // Laplacian variance: high = sharp, low = blurry.
  // Computed on the raw ROI grayscale BEFORE preprocessing.
  // If below BLUR_THRESHOLD, we skip OCR entirely (save CPU, avoid garbage).
  function measureBlur(grayData, w, h) {
    var sumSq = 0, count = 0;
    for (var ly = 1; ly < h - 1; ly++) {
      for (var lx = 1; lx < w - 1; lx++) {
        // Laplacian kernel: [0,1,0; 1,-4,1; 0,1,0]
        var lap = -4 * grayData[ly * w + lx]
                + grayData[(ly - 1) * w + lx]
                + grayData[(ly + 1) * w + lx]
                + grayData[ly * w + (lx - 1)]
                + grayData[ly * w + (lx + 1)];
        sumSq += lap * lap;
        count++;
      }
    }
    return count > 0 ? sumSq / count : 0;
  }

  // ── ROI Capture ────────────────────────────────────────────────────────────
  // Captures from the full-resolution video frame (not the scaled CSS preview).
  // Crops to the plate band: the middle rows of the guide box, cutting
  // state name (top) and slogan (bottom).
  function captureROI() {
    if (!videoEl || !canvasEl) return null;
    var vw = videoEl.videoWidth;
    var vh = videoEl.videoHeight;
    if (!vw || !vh) return null;

    // Full ROI matching the CSS guide box
    var fullW = Math.round(vw * ROI_WIDTH_RATIO);
    var fullH = Math.round(vh * ROI_HEIGHT_RATIO);
    var fullX = Math.round((vw - fullW) / 2);
    var fullY = Math.round((vh - fullH) / 2);

    // Plate band: crop to rows PLATE_BAND_TOP..PLATE_BAND_BOTTOM
    var bandTopPx = Math.round(fullH * PLATE_BAND_TOP);
    var bandBotPx = Math.round(fullH * PLATE_BAND_BOTTOM);
    var bandH = bandBotPx - bandTopPx;
    var bandY = fullY + bandTopPx;

    canvasEl.width = fullW;
    canvasEl.height = bandH;
    var ctx = canvasEl.getContext('2d');
    ctx.drawImage(videoEl, fullX, bandY, fullW, bandH, 0, 0, fullW, bandH);

    return canvasEl;
  }

  // ── Dual-Pass OCR ──────────────────────────────────────────────────────────
  // Pass 1: PSM 7 (single text line) – optimal for the plate band.
  // If confidence < 60%, tries PSM 8 (single word) as fallback.
  // Takes the result with higher confidence.
  async function dualPassOCR(canvas) {
    if (!ocrWorker) await initOCRWorker();

    // Pass 1: PSM 7 (already set as default)
    var r1 = await ocrWorker.recognize(canvas);
    var t1 = (r1.data.text || '').trim();
    var c1 = r1.data.confidence || 0;

    // Fast path: high confidence → skip second pass
    if (c1 >= 60) {
      return { text: t1, confidence: c1 };
    }

    // Pass 2: PSM 8 (single word) – may read better for short plates
    await ocrWorker.setParameters({ tessedit_pageseg_mode: '8' });
    var r2 = await ocrWorker.recognize(canvas);
    var t2 = (r2.data.text || '').trim();
    var c2 = r2.data.confidence || 0;

    // Restore PSM 7 for next frame
    await ocrWorker.setParameters({ tessedit_pageseg_mode: '7' });

    console.log('[PlateScanner] Dual-pass: PSM7="' + t1 + '"(' + Math.round(c1) + '%) PSM8="' + t2 + '"(' + Math.round(c2) + '%)');

    return c2 > c1 ? { text: t2, confidence: c2 } : { text: t1, confidence: c1 };
  }

  // ── Temporal Voting ────────────────────────────────────────────────────────
  // Instead of requiring N consecutive identical reads (fragile), we use
  // frequency-based voting over a rolling window.  This handles the reality
  // that OCR results fluctuate frame-to-frame.
  //
  // Accept if:
  //   - Same text appears ≥ VOTE_THRESHOLD times in window, OR
  //   - Same text appears ≥ HIGH_CONF_VOTES times with conf ≥ HIGH_CONF_THRESHOLD
  function temporalVote() {
    if (voteWindow.length < HIGH_CONF_VOTES) return null;

    var counts = {};
    var highCounts = {};
    for (var vi = 0; vi < voteWindow.length; vi++) {
      var vt = voteWindow[vi].text;
      if (!vt) continue;
      counts[vt] = (counts[vt] || 0) + 1;
      if (voteWindow[vi].confidence >= HIGH_CONF_THRESHOLD) {
        highCounts[vt] = (highCounts[vt] || 0) + 1;
      }
    }

    var bestText = null, bestCount = 0;
    for (var text in counts) {
      if (counts[text] > bestCount) {
        bestCount = counts[text];
        bestText = text;
      }
    }

    if (!bestText) return null;
    if (bestCount >= VOTE_THRESHOLD) return bestText;
    if ((highCounts[bestText] || 0) >= HIGH_CONF_VOTES) return bestText;

    return null;
  }

  function countVotesFor(text) {
    var cnt = 0;
    for (var ci = 0; ci < voteWindow.length; ci++) {
      if (voteWindow[ci].text === text) cnt++;
    }
    return cnt;
  }

  // ── Camera ─────────────────────────────────────────────────────────────────
  async function startCamera() {
    setState('requesting');
    try {
      var constraints = {
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      };
      videoStream = await navigator.mediaDevices.getUserMedia(constraints);
      videoEl.srcObject = videoStream;
      await videoEl.play();
      setState('streaming');
      setTimeout(function () {
        if (currentState === 'streaming') {
          setState('scanning');
          startOCRLoop();
        }
      }, 600);
    } catch (err) {
      console.error('[PlateScanner] Camera error:', err.name, err.message);
      handleCameraError(err);
    }
  }

  function stopCamera() {
    if (ocrTimer) { clearTimeout(ocrTimer); ocrTimer = null; }
    isProcessing = false;
    if (videoStream) {
      videoStream.getTracks().forEach(function (t) { t.stop(); });
      videoStream = null;
    }
    if (videoEl) { videoEl.srcObject = null; }
  }

  function handleCameraError(err) {
    var title = 'Camera Unavailable';
    var msg = 'Unable to access camera.';
    if (err.name === 'NotAllowedError') {
      title = 'Camera Permission Denied';
      msg = 'Please allow camera access in your browser settings, then try again.';
    } else if (err.name === 'NotFoundError') {
      title = 'No Camera Found';
      msg = 'No camera detected on this device.';
    } else if (err.name === 'NotReadableError') {
      title = 'Camera Busy';
      msg = 'Your camera is being used by another app. Please close it and try again.';
    }
    showFallbackUI(title, msg);
  }

  // ── OCR Loop ───────────────────────────────────────────────────────────────
  function startOCRLoop() {
    if (currentState !== 'scanning') return;
    voteWindow = [];
    isProcessing = false;
    runOCRFrame();
  }

  async function runOCRFrame() {
    if (currentState !== 'scanning' || !videoEl || videoEl.readyState < 2) {
      ocrTimer = setTimeout(runOCRFrame, OCR_INTERVAL_MS);
      return;
    }

    // Guard against overlapping calls (OCR takes 300-800ms)
    if (isProcessing) {
      ocrTimer = setTimeout(runOCRFrame, 100);
      return;
    }
    isProcessing = true;

    try {
      // 1. Capture plate band from full-res video
      var roi = captureROI();
      if (!roi) {
        isProcessing = false;
        ocrTimer = setTimeout(runOCRFrame, OCR_INTERVAL_MS);
        return;
      }

      // 2. Quick blur check on raw ROI (before expensive preprocessing)
      var roiCtx = roi.getContext('2d');
      var roiPixels = roiCtx.getImageData(0, 0, roi.width, roi.height);
      var quickGray = new Uint8Array(roi.width * roi.height);
      for (var bi = 0, bj = 0; bi < roiPixels.data.length; bi += 4, bj++) {
        quickGray[bj] = Math.round(
          0.299 * roiPixels.data[bi] +
          0.587 * roiPixels.data[bi + 1] +
          0.114 * roiPixels.data[bi + 2]
        );
      }
      var blurScore = measureBlur(quickGray, roi.width, roi.height);

      if (blurScore < BLUR_THRESHOLD) {
        updateScanStatus(null, 0, true);  // "Hold steady" message
        isProcessing = false;
        ocrTimer = setTimeout(runOCRFrame, OCR_INTERVAL_MS);
        return;
      }

      // 3. Full preprocessing pipeline
      var processed = preprocessForOCR(roi);

      // 4. Dual-pass OCR (PSM 7 primary, PSM 8 fallback)
      if (!ocrWorker) await initOCRWorker();
      var result = await dualPassOCR(processed);
      var rawText = result.text;
      var confidence = result.confidence;
      var cleaned = cleanPlateText(rawText);

      if (rawText) {
        console.log('[PlateScanner] "' + cleaned + '" conf:' + Math.round(confidence) +
          '% blur:' + Math.round(blurScore) + ' votes:' + countVotesFor(cleaned) + '/' + VOTE_THRESHOLD);
      }

      // 5. Update live preview
      updateScanStatus(cleaned, confidence, false);

      // 6. Add to vote window and check for winner
      if (cleaned && isValidPlateCandidate(cleaned) && confidence >= MIN_CONFIDENCE) {
        voteWindow.push({ text: cleaned, confidence: confidence });
        if (voteWindow.length > VOTE_WINDOW_SIZE) voteWindow.shift();

        var winner = temporalVote();
        if (winner) {
          isProcessing = false;
          onPlateDetected(winner);
          return;
        }
      }
    } catch (err) {
      console.warn('[PlateScanner] OCR frame error:', err.message);
    }

    isProcessing = false;
    ocrTimer = setTimeout(runOCRFrame, OCR_INTERVAL_MS);
  }

  // ── Plate Detected ─────────────────────────────────────────────────────────
  function onPlateDetected(plateText) {
    if (ocrTimer) { clearTimeout(ocrTimer); ocrTimer = null; }
    setState('candidate');

    if (navigator.vibrate) {
      try { navigator.vibrate(100); } catch (e) { /* ignore */ }
    }

    showConfirmUI(plateText);
  }

  // ── UI ─────────────────────────────────────────────────────────────────────
  function createOverlay() {
    if (overlayEl) { overlayEl.remove(); }

    overlayEl = document.createElement('div');
    overlayEl.className = 'lps-overlay';
    overlayEl.innerHTML = [
      '<div class="lps-overlay__inner">',
        // Header
        '<div class="lps-header">',
          '<button type="button" class="lps-close-btn" aria-label="Close scanner">',
            '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>',
          '</button>',
          '<span class="lps-header__title">Scan License Plate</span>',
          '<div class="lps-header__spacer"></div>',
        '</div>',
        // Video container
        '<div class="lps-video-container">',
          '<video class="lps-video" autoplay playsinline muted></video>',
          '<div class="lps-guide">',
            '<div class="lps-guide__box">',
              '<div class="lps-guide__corner lps-guide__corner--tl"></div>',
              '<div class="lps-guide__corner lps-guide__corner--tr"></div>',
              '<div class="lps-guide__corner lps-guide__corner--bl"></div>',
              '<div class="lps-guide__corner lps-guide__corner--br"></div>',
            '</div>',
            '<div class="lps-guide__hint">Fill the box with your plate</div>',
          '</div>',
          '<div class="lps-scan-status">',
            '<span class="lps-scan-status__text">Initializing camera...</span>',
          '</div>',
        '</div>',
        // Confirm section
        '<div class="lps-confirm" style="display:none;">',
          '<div class="lps-confirm__card">',
            '<div class="lps-confirm__label">Detected Plate</div>',
            '<input type="text" class="lps-confirm__plate-input" maxlength="8" autocapitalize="characters" spellcheck="false" />',
            '<div class="lps-confirm__state-row">',
              '<label class="lps-confirm__state-label">State</label>',
              '<select class="lps-confirm__state-select">',
                '<option value="">Select State</option>',
              '</select>',
            '</div>',
            '<div class="lps-confirm__actions">',
              '<button type="button" class="lps-confirm__btn lps-confirm__btn--rescan">Rescan</button>',
              '<button type="button" class="lps-confirm__btn lps-confirm__btn--confirm">Confirm & Find Colors</button>',
            '</div>',
          '</div>',
        '</div>',
        // Fallback section
        '<div class="lps-fallback" style="display:none;">',
          '<div class="lps-fallback__card">',
            '<div class="lps-fallback__icon">',
              '<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">',
                '<rect x="1" y="6" width="22" height="12" rx="2" ry="2"/>',
                '<line x1="5" y1="12" x2="5.01" y2="12"/>',
                '<line x1="19" y1="12" x2="19.01" y2="12"/>',
              '</svg>',
            '</div>',
            '<div class="lps-fallback__title">Camera Unavailable</div>',
            '<div class="lps-fallback__msg"></div>',
            '<div class="lps-fallback__or">Enter plate manually instead</div>',
            '<input type="text" class="lps-fallback__plate-input" placeholder="Plate #" maxlength="8" autocapitalize="characters" spellcheck="false" />',
            '<div class="lps-fallback__state-row">',
              '<select class="lps-fallback__state-select">',
                '<option value="">Select State</option>',
              '</select>',
            '</div>',
            '<div class="lps-fallback__or-upload">or</div>',
            '<label class="lps-fallback__upload-btn">',
              '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">',
                '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>',
                '<polyline points="17 8 12 3 7 8"/>',
                '<line x1="12" y1="3" x2="12" y2="15"/>',
              '</svg>',
              'Upload a Photo of Your Plate',
              '<input type="file" accept="image/*" capture="environment" class="lps-fallback__file-input" style="display:none;" />',
            '</label>',
            '<div class="lps-fallback__actions">',
              '<button type="button" class="lps-confirm__btn lps-confirm__btn--confirm lps-fallback__confirm-btn" disabled>Find My Color</button>',
            '</div>',
          '</div>',
        '</div>',
        // Loading overlay
        '<div class="lps-loading" style="display:none;">',
          '<div class="lps-loading__spinner"></div>',
          '<span class="lps-loading__text">Loading OCR engine...</span>',
        '</div>',
      '</div>',
    ].join('');

    // Hidden canvas for ROI capture
    canvasEl = document.createElement('canvas');
    canvasEl.style.display = 'none';
    overlayEl.appendChild(canvasEl);

    document.body.appendChild(overlayEl);

    videoEl = overlayEl.querySelector('.lps-video');

    // Populate state selects
    var stateOptions = US_STATES.map(function (s) {
      return '<option value="' + s.code + '">' + s.code + ' - ' + s.name + '</option>';
    }).join('');

    overlayEl.querySelectorAll('.lps-confirm__state-select, .lps-fallback__state-select').forEach(function (sel) {
      sel.innerHTML = '<option value="">Select State</option>' + stateOptions;
      var lastState = getLastUsedState();
      if (lastState) sel.value = lastState;
    });

    // Bind events
    overlayEl.querySelector('.lps-close-btn').addEventListener('click', close);

    overlayEl.querySelector('.lps-confirm__btn--rescan').addEventListener('click', function () {
      hideConfirmUI();
      setState('scanning');
      startOCRLoop();
    });

    overlayEl.querySelector('.lps-confirm__btn--confirm').addEventListener('click', function () {
      var plate = overlayEl.querySelector('.lps-confirm__plate-input').value.toUpperCase().replace(/[^A-Z0-9]/g, '');
      var state = overlayEl.querySelector('.lps-confirm__state-select').value;
      if (!plate || plate.length < MIN_PLATE_LENGTH) {
        shakeElement(overlayEl.querySelector('.lps-confirm__plate-input'));
        return;
      }
      if (!state) {
        shakeElement(overlayEl.querySelector('.lps-confirm__state-select'));
        return;
      }
      saveLastUsedState(state);
      onConfirmed(plate, state);
    });

    // Fallback flow
    var fallbackPlate = overlayEl.querySelector('.lps-fallback__plate-input');
    var fallbackState = overlayEl.querySelector('.lps-fallback__state-select');
    var fallbackConfirm = overlayEl.querySelector('.lps-fallback__confirm-btn');

    function updateFallbackBtn() {
      var pVal = fallbackPlate.value.replace(/[^A-Za-z0-9]/g, '');
      var sVal = fallbackState.value;
      fallbackConfirm.disabled = !(pVal.length >= MIN_PLATE_LENGTH && sVal);
    }
    fallbackPlate.addEventListener('input', updateFallbackBtn);
    fallbackState.addEventListener('change', updateFallbackBtn);

    fallbackConfirm.addEventListener('click', function () {
      var plate = fallbackPlate.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
      var state = fallbackState.value;
      if (!plate || !state) return;
      saveLastUsedState(state);
      onConfirmed(plate, state);
    });

    // File upload OCR
    var fileInput = overlayEl.querySelector('.lps-fallback__file-input');
    fileInput.addEventListener('change', function (e) {
      var file = e.target.files && e.target.files[0];
      if (!file) return;
      processUploadedImage(file);
    });

    document.body.style.overflow = 'hidden';
    return overlayEl;
  }

  function removeOverlay() {
    if (overlayEl) {
      overlayEl.remove();
      overlayEl = null;
      videoEl = null;
      canvasEl = null;
    }
    procCanvas = null;
    document.body.style.overflow = '';
  }

  function setState(newState) {
    currentState = newState;
    if (!overlayEl) return;

    var statusEl = overlayEl.querySelector('.lps-scan-status__text');
    var guideHint = overlayEl.querySelector('.lps-guide__hint');
    var guideBox = overlayEl.querySelector('.lps-guide__box');
    var loadingEl = overlayEl.querySelector('.lps-loading');

    switch (newState) {
      case 'requesting':
        if (statusEl) statusEl.textContent = 'Requesting camera access...';
        if (loadingEl) loadingEl.style.display = 'flex';
        break;
      case 'streaming':
        if (statusEl) statusEl.textContent = 'Camera ready. Loading OCR...';
        if (loadingEl) loadingEl.style.display = 'flex';
        break;
      case 'scanning':
        if (statusEl) statusEl.textContent = 'Scanning for plate...';
        if (guideHint) guideHint.textContent = 'Fill the box with your plate';
        if (guideBox) {
          guideBox.classList.add('lps-guide__box--scanning');
          guideBox.classList.remove('lps-guide__box--blurry');
        }
        if (loadingEl) loadingEl.style.display = 'none';
        break;
      case 'candidate':
        if (guideBox) {
          guideBox.classList.remove('lps-guide__box--scanning');
          guideBox.classList.remove('lps-guide__box--blurry');
          guideBox.classList.add('lps-guide__box--found');
        }
        if (statusEl) statusEl.textContent = 'Plate detected!';
        break;
      case 'confirm':
        break;
      case 'done':
        break;
    }
  }

  function updateScanStatus(text, confidence, isBlurry) {
    if (!overlayEl) return;
    var statusEl = overlayEl.querySelector('.lps-scan-status__text');
    var guideHint = overlayEl.querySelector('.lps-guide__hint');
    var guideBox = overlayEl.querySelector('.lps-guide__box');
    if (!statusEl) return;

    if (isBlurry) {
      statusEl.textContent = 'Hold steady...';
      if (guideHint) guideHint.textContent = 'Hold your phone still';
      if (guideBox) guideBox.classList.add('lps-guide__box--blurry');
      return;
    }

    if (guideBox) guideBox.classList.remove('lps-guide__box--blurry');

    if (text) {
      var votes = countVotesFor(text);
      statusEl.textContent = 'Reading: ' + text + ' (' + votes + '/' + VOTE_THRESHOLD + ')';
      if (guideHint) {
        if (votes >= VOTE_THRESHOLD - 1) {
          guideHint.textContent = 'Almost there...';
        } else {
          guideHint.textContent = 'Keep plate in the box';
        }
      }
    } else {
      statusEl.textContent = 'Scanning for plate...';
      if (guideHint) guideHint.textContent = 'Fill the box with your plate';
    }
  }

  function showConfirmUI(plateText) {
    if (!overlayEl) return;
    setState('confirm');

    var videoContainer = overlayEl.querySelector('.lps-video-container');
    var confirmSection = overlayEl.querySelector('.lps-confirm');

    videoContainer.style.display = 'none';
    confirmSection.style.display = 'flex';

    var plateInput = overlayEl.querySelector('.lps-confirm__plate-input');
    plateInput.value = plateText;
    plateInput.focus({ preventScroll: true });
    plateInput.select();

    var stateSelect = overlayEl.querySelector('.lps-confirm__state-select');
    var lastState = getLastUsedState();
    if (lastState && !stateSelect.value) {
      stateSelect.value = lastState;
    }
  }

  function hideConfirmUI() {
    if (!overlayEl) return;
    var videoContainer = overlayEl.querySelector('.lps-video-container');
    var confirmSection = overlayEl.querySelector('.lps-confirm');
    var guideBox = overlayEl.querySelector('.lps-guide__box');

    confirmSection.style.display = 'none';
    videoContainer.style.display = 'flex';
    if (guideBox) {
      guideBox.classList.remove('lps-guide__box--found');
    }
    voteWindow = [];
  }

  function showFallbackUI(title, msg) {
    if (!overlayEl) return;
    stopCamera();

    var videoContainer = overlayEl.querySelector('.lps-video-container');
    var fallbackSection = overlayEl.querySelector('.lps-fallback');
    var loadingEl = overlayEl.querySelector('.lps-loading');

    videoContainer.style.display = 'none';
    if (loadingEl) loadingEl.style.display = 'none';
    fallbackSection.style.display = 'flex';

    fallbackSection.querySelector('.lps-fallback__title').textContent = title;
    fallbackSection.querySelector('.lps-fallback__msg').textContent = msg;
  }

  function shakeElement(el) {
    if (!el) return;
    el.classList.add('lps-shake');
    setTimeout(function () { el.classList.remove('lps-shake'); }, 500);
  }

  // ── Photo Upload OCR ───────────────────────────────────────────────────────
  async function processUploadedImage(file) {
    if (!overlayEl) return;
    var loadingEl = overlayEl.querySelector('.lps-loading');
    var loadingText = overlayEl.querySelector('.lps-loading__text');

    if (loadingEl) loadingEl.style.display = 'flex';
    if (loadingText) loadingText.textContent = 'Reading plate from photo...';

    try {
      if (!ocrWorker) {
        await initOCRWorker();
      }

      var img = await createImageBitmap(file);
      var tempCanvas = document.createElement('canvas');
      tempCanvas.width = img.width;
      tempCanvas.height = img.height;
      var ctx = tempCanvas.getContext('2d');
      ctx.drawImage(img, 0, 0);

      // Use the full preprocessing pipeline for uploaded photos too
      var processed = preprocessForOCR(tempCanvas);

      // Use PSM 6 for uploaded photos (may contain full plate with state/slogan)
      await ocrWorker.setParameters({ tessedit_pageseg_mode: '6' });
      var result = await ocrWorker.recognize(processed);
      // Restore PSM 7 for camera mode
      await ocrWorker.setParameters({ tessedit_pageseg_mode: '7' });

      var cleaned = cleanPlateText(result.data.text || '');

      if (loadingEl) loadingEl.style.display = 'none';

      if (cleaned && cleaned.length >= MIN_PLATE_LENGTH) {
        var fallbackPlate = overlayEl.querySelector('.lps-fallback__plate-input');
        fallbackPlate.value = cleaned;
        fallbackPlate.dispatchEvent(new Event('input'));

        var msgEl = overlayEl.querySelector('.lps-fallback__msg');
        msgEl.textContent = 'Detected: ' + cleaned + ' (edit if needed)';
        msgEl.style.color = '#6DC3F7';
      } else {
        var msgEl2 = overlayEl.querySelector('.lps-fallback__msg');
        msgEl2.textContent = 'Could not read plate from photo. Please enter it manually.';
        msgEl2.style.color = '#f87171';
      }
    } catch (err) {
      console.error('[PlateScanner] Photo OCR error:', err);
      if (loadingEl) loadingEl.style.display = 'none';
      var msgEl3 = overlayEl.querySelector('.lps-fallback__msg');
      msgEl3.textContent = 'Error reading photo. Please enter plate manually.';
    }
  }

  // ── Confirm & Close ────────────────────────────────────────────────────────
  function onConfirmed(plate, state) {
    setState('done');
    stopCamera();

    if (window.DYCAnalytics && typeof window.DYCAnalytics.track === 'function') {
      window.DYCAnalytics.track('custom', {
        category: 'make_model_year_finder',
        action: 'plate_scanned_confirmed',
        label: 'OCR scan',
        metadata: { plateLength: plate.length, state: state }
      });
    }

    var cb = callbacks.onResult;
    removeOverlay();
    if (cb) cb({ plate: plate, state: state });
  }

  function close() {
    stopCamera();
    setState('idle');

    if (window.DYCAnalytics && typeof window.DYCAnalytics.track === 'function') {
      window.DYCAnalytics.track('custom', {
        category: 'make_model_year_finder',
        action: 'plate_scanner_closed',
        label: 'user_cancelled'
      });
    }

    var cb = callbacks.onCancel;
    removeOverlay();
    if (cb) cb();
  }

  // ── State persistence ──────────────────────────────────────────────────────
  function getLastUsedState() {
    try { return localStorage.getItem('dyc_plate_last_state') || ''; }
    catch (e) { return ''; }
  }

  function saveLastUsedState(code) {
    try { localStorage.setItem('dyc_plate_last_state', code); }
    catch (e) { /* ignore */ }
  }

  // ── Feature detection ──────────────────────────────────────────────────────
  function isCameraSupported() {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
  }

  // ── Public API ─────────────────────────────────────────────────────────────
  async function open(opts) {
    opts = opts || {};
    callbacks.onResult = opts.onResult || null;
    callbacks.onCancel = opts.onCancel || null;

    if (window.DYCAnalytics && typeof window.DYCAnalytics.track === 'function') {
      window.DYCAnalytics.track('custom', {
        category: 'make_model_year_finder',
        action: 'plate_scanner_opened',
        label: isCameraSupported() ? 'camera_supported' : 'no_camera'
      });
    }

    createOverlay();

    if (!isCameraSupported()) {
      showFallbackUI(
        'Camera Not Available',
        'Your browser does not support camera access. You can enter your plate manually or upload a photo.'
      );
      return;
    }

    var ocrReady = initOCRWorker().catch(function (err) {
      console.warn('[PlateScanner] OCR preload failed, will retry:', err.message);
    });

    await startCamera();
    await ocrReady;
  }

  // ── Expose API ─────────────────────────────────────────────────────────────
  window.LicensePlateScanner = {
    open: open,
    close: close,
    isSupported: isCameraSupported,
  };

  console.log('[PlateScanner] v2 loaded. Camera supported:', isCameraSupported());
})();
